// Forecast Planner — the week-over-week sibling of the Forecast Lock page.
//
// Same data + lock workflow, but every section breaks the 4-week window out
// PER WEEK (W1..W4) so comparison is easy, each week expands to its 7 days on
// click, and there's a dedicated Events & Reroutes section so nothing is missed.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Field, Input, PageHeader, Select, Spinner } from "../../components/ui";
import { BarsChart, DailyBarsLine, HourBars } from "../../components/charts";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtDow, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Branch, Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import { forecastCustomerHub, forecastHubDay, meanAbsPctError, reroutedInToHub, reroutedOutOfHub } from "../../lib/forecast/engine";
import { hourlyAvailability, lhLoadsInOnDay } from "../../lib/forecast/arrivals";
import { addPackageSplit, emptyPackageSplit, splitPackageMix } from "../../lib/forecast/hubMetrics";
import { flowShares, shareOf } from "../../lib/forecast/flowShares";
import { makeTransitFn, receivedInByDay } from "../../lib/forecast/transit";
import { useTransitAware } from "../../lib/forecast/useTransitAware";
import { buildLockCells, lockedHubWeekTotals, weekDates, type LockCell, type LockFlow } from "../../lib/forecast/lockSnapshot";
import {
  createLock,
  getActiveLockForWeek,
  getLockCells,
  listActiveLocks,
  unlockWeek,
  type ForecastLock,
} from "./forecastLockApi";
import { getFlowShareTotals, type BranchFlow } from "../actuals/branchFlowApi";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listBranches } from "../branches/branchesApi";
import { listHubs } from "../hubs/hubsApi";
import { listAccountLhSchedule, type LhLaneRow } from "../lhschedule/lhScheduleApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "./overridesApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";

const ALL = "__all__";
const WEEK_OPTIONS = 9;
const TYPE_COLORS = { cartons: "#0f766e", polybags: "#0ea5e9", irregular: "#f59e0b", unspec: "#cbd5e1" } as const;
const WEEK_COLORS = ["#0f766e", "#0ea5e9", "#a855f7", "#f59e0b"];
const HUB_PALETTE = ["#0f766e", "#0ea5e9", "#a855f7", "#f59e0b", "#ec4899", "#10b981", "#6366f1", "#ef4444", "#14b8a6", "#f97316", "#8b5cf6", "#84cc16"];
const EVENT_LABELS: Record<string, string> = {
  closure: "Closure",
  reduced: "Reduced capacity",
  expansion: "Expansion",
  reroute: "Reroute",
  weather: "Weather",
};

type Split = { cartons: number; polybags: number; irregular: number; unspec: number };
type Col = { key: string; label: string; sub?: string; dates: string[]; weekStart: string; isDay: boolean; first: boolean };
type Row = { id: string; label: string; sub?: string; byDate: Map<string, number>; right?: ReactNode; color?: string; cap?: number | null };

export default function ForecastPlannerPage() {
  const { activeAccount, session } = useAuth();
  const accountId = activeAccount?.accountId ?? null;
  const isAdmin = activeAccount?.role === "admin";

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [lhRows, setLhRows] = useState<LhLaneRow[]>([]);
  const [liveFlows, setLiveFlows] = useState<BranchFlow[]>([]);
  const [locks, setLocks] = useState<ForecastLock[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);

  const [hubId, setHubId] = useState(ALL);
  const [startWeek, setStartWeek] = useState(() => startOfWeekSat(addDays(today(), 7)));
  const [topN, setTopN] = useState(8);
  const [transitAware, setTransitAware] = useTransitAware();
  const [openWeeks, setOpenWeeks] = useState<Set<string>>(new Set());
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");

  const [lock, setLock] = useState<ForecastLock | null>(null);
  const [lockCells, setLockCells] = useState<LockCell[]>([]);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listBranches(accountId),
      listAccountLanes(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountLhSchedule(accountId),
      listActiveLocks(accountId),
    ])
      .then(([c, h, b, ln, ov, ev, lh, lk]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setBranches(b.filter((x) => x.active));
        setLanes(ln);
        setOverrides(ov);
        setEvents(ev);
        setLhRows(lh);
        setLocks(lk);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load planning data.")))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    getFlowShareTotals(accountId, addDays(startWeek, -28), addDays(startWeek, -1))
      .then(setLiveFlows)
      .catch(() => setLiveFlows([]));
  }, [accountId, startWeek]);

  // Trailing 4 COMPLETED weeks of actuals (relative to the current week, so the
  // in-progress week's empty actuals don't distort the comparison).
  const nowWeek = useMemo(() => startOfWeekSat(today()), []);
  useEffect(() => {
    if (!accountId) return;
    listAccountCustomerActuals(accountId, addDays(nowWeek, -28), addDays(nowWeek, -1))
      .then(setActuals)
      .catch(() => setActuals([]));
  }, [accountId, nowWeek]);

  const refreshLock = useCallback(async () => {
    if (!accountId) return;
    const lk = await getActiveLockForWeek(accountId, startWeek);
    setLock(lk);
    setLockCells(lk ? await getLockCells(lk.id) : []);
  }, [accountId, startWeek]);

  useEffect(() => {
    refreshLock().catch((e) => setError(getErrorMessage(e, "Failed to load the lock.")));
  }, [refreshLock]);

  const isAll = hubId === ALL;
  const hubSet = useMemo(() => (isAll ? new Set(hubs.map((h) => h.id)) : new Set([hubId])), [isAll, hubs, hubId]);
  // Stable per-hub color so a hub reads the same across every table/legend.
  const hubColor = useMemo(() => {
    const m = new Map<string, string>();
    hubs.forEach((h, i) => m.set(h.id, HUB_PALETTE[i % HUB_PALETTE.length]));
    return m;
  }, [hubs]);
  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const laneMix = useMemo(() => {
    const m = new Map<string, { cartons: number; polybags: number; irregular: number }>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.packageMix);
    return m;
  }, [lanes]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);

  const weeks4 = useMemo(
    () =>
      Array.from({ length: 4 }, (_, k) => {
        const ws = addDays(startWeek, k * 7);
        return { weekStart: ws, weekEnd: addDays(ws, 6), dates: weekDates(ws) };
      }),
    [startWeek]
  );
  const allDates = useMemo(() => weeks4.flatMap((w) => w.dates), [weeks4]);
  const lockWeek = weeks4[0].weekStart;

  const cFcst = useMemo(() => {
    const cache = new Map<string, number>();
    return (c: Customer, h: string, d: string) => {
      const k = `${c.id}|${h}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = forecastCustomerHub(c, h, d, overrideMap, events);
        cache.set(k, v);
      }
      return v;
    };
  }, [overrideMap, events]);

  // ── One pass: hub-day own (all hubs) + filtered customer-day + package per week/day ──
  const plan = useMemo(() => {
    const allHubIds = hubs.map((h) => h.id);
    const hubDayOwn = new Map<string, number>(); // hub|date
    const custDate = new Map<string, number>(); // cust|date (filtered)
    const custHub = new Map<string, number>(); // cust|hub (filtered) — for primary-hub color
    const custTotal = new Map<string, number>();
    const custName = new Map<string, string>();
    const pkgWeek: Split[] = Array.from({ length: 4 }, () => emptyPackageSplit());
    const dayPkg = new Map<string, Split>();
    const hubPkg = new Map<string, Split>(); // hubId → 4-week package split (filtered)
    let pkgAll: Split = emptyPackageSplit();

    for (const h of allHubIds) {
      weeks4.forEach((w, wi) => {
        for (const d of w.dates) {
          let own = 0;
          for (const c of customers) {
            const v = cFcst(c, h, d);
            if (v <= 0) continue;
            own += v;
            if (hubSet.has(h)) {
              custDate.set(`${c.id}|${d}`, (custDate.get(`${c.id}|${d}`) ?? 0) + v);
              custHub.set(`${c.id}|${h}`, (custHub.get(`${c.id}|${h}`) ?? 0) + v);
              custTotal.set(c.id, (custTotal.get(c.id) ?? 0) + v);
              custName.set(c.id, c.name);
              const sp = splitPackageMix(v, laneMix.get(`${c.id}|${h}`));
              pkgWeek[wi] = addPackageSplit(pkgWeek[wi], sp);
              pkgAll = addPackageSplit(pkgAll, sp);
              dayPkg.set(d, addPackageSplit(dayPkg.get(d) ?? emptyPackageSplit(), sp));
              hubPkg.set(h, addPackageSplit(hubPkg.get(h) ?? emptyPackageSplit(), sp));
            }
          }
          hubDayOwn.set(`${h}|${d}`, own);
        }
      });
    }
    const weekTot = weeks4.map((w) => w.dates.reduce((s, d) => s + [...hubSet].reduce((a, h) => a + (hubDayOwn.get(`${h}|${d}`) ?? 0), 0), 0));
    return { hubDayOwn, custDate, custHub, custTotal, custName, pkgWeek, dayPkg, hubPkg, pkgAll, weekTot };
  }, [hubs, customers, weeks4, hubSet, cFcst, laneMix]);

  // Trailing 3 weeks of ACTUAL volume (filtered hubs), aggregated per week.
  const actualWeeks = useMemo(() => {
    const starts = [addDays(nowWeek, -21), addDays(nowWeek, -14), addDays(nowWeek, -7)];
    return starts.map((ws) => {
      const wEnd = addDays(ws, 6);
      let total = 0;
      for (const a of actuals) {
        if (a.date >= ws && a.date <= wEnd && hubSet.has(a.hubId)) total += a.actualVolume;
      }
      return { weekStart: ws, weekEnd: wEnd, total };
    });
  }, [actuals, nowWeek, hubSet]);

  // Daily MHE sortation caps for the filtered hubs, by package type.
  const mheCaps = useMemo(() => {
    const sel = hubs.filter((h) => hubSet.has(h.id));
    const sumCap = (pick: (h: Hub) => number | null | undefined): number | null => {
      const vals = sel.map(pick).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
    };
    return { cartons: sumCap((h) => h.mheCartons), polybags: sumCap((h) => h.mhePolybag), irregular: sumCap((h) => h.mheIrregular) };
  }, [hubs, hubSet]);

  const shares = useMemo(() => flowShares(liveFlows), [liveFlows]);

  // Branch per-date (network flow) for branches in scope. Transit-aware: an origin's
  // ship-date volume arrives at the branch on date + transit (look-back fetches origin
  // own for ship dates before the window).
  const branchDate = useMemo(() => {
    const allHubIds = hubs.map((h) => h.id);
    const tFn = makeTransitFn(hubs, branches, transitAware);
    const lookCache = new Map<string, number>();
    const ownDay = (o: string, d: string) => {
      const inWin = plan.hubDayOwn.get(`${o}|${d}`);
      if (inWin !== undefined) return inWin;
      const k = `${o}|${d}`;
      let v = lookCache.get(k);
      if (v === undefined) {
        v = customers.reduce((s, c) => s + cFcst(c, o, d), 0);
        lookCache.set(k, v);
      }
      return v;
    };
    const m = new Map<string, Map<string, number>>(); // branchId → (date → value)
    for (const b of branches) {
      if (!isAll && !hubSet.has(b.hubId)) continue;
      const bd = new Map<string, number>();
      let any = 0;
      for (const d of allDates) {
        let v = 0;
        for (const o of allHubIds) {
          const s = shareOf(shares, o, b.id);
          if (s <= 0) continue;
          v += ownDay(o, transitAware ? addDays(d, -tFn(o, b.id)) : d) * s;
        }
        bd.set(d, v);
        any += v;
      }
      if (any > 0) m.set(b.id, bd);
    }
    return m;
  }, [branches, hubs, isAll, hubSet, allDates, plan.hubDayOwn, shares, transitAware, customers, cFcst]);

  // 4-week own forecast per hub (all hubs) — origins that feed branches.
  const hubOwn4 = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of hubs) m.set(h.id, allDates.reduce((s, d) => s + (plan.hubDayOwn.get(`${h.id}|${d}`) ?? 0), 0));
    return m;
  }, [hubs, allDates, plan.hubDayOwn]);

  // Per-hub, per-day SORTATION load = own + H2H received-in (everything the hub
  // sorts). Capacity / MHE checks use this, not just own.
  const receivedByHubDay = useMemo(() => {
    const allHubIds = hubs.map((h) => h.id);
    const tFn = makeTransitFn(hubs, branches, transitAware);
    const lookCache = new Map<string, number>();
    const ownDay = (o: string, d: string) => {
      const inWin = plan.hubDayOwn.get(`${o}|${d}`);
      if (inWin !== undefined) return inWin;
      const k = `${o}|${d}`;
      let v = lookCache.get(k);
      if (v === undefined) {
        v = customers.reduce((s, c) => s + cFcst(c, o, d), 0);
        lookCache.set(k, v);
      }
      return v;
    };
    return receivedInByDay({ hubIds: allHubIds, branches, dates: allDates, own: ownDay, share: (o, b) => shareOf(shares, o, b), transit: (o, b) => tFn(o, b) });
  }, [hubs, branches, transitAware, allDates, plan.hubDayOwn, shares, customers, cFcst]);
  const sortDayOf = useCallback(
    (hubIdArg: string, d: string) => (plan.hubDayOwn.get(`${hubIdArg}|${d}`) ?? 0) + (receivedByHubDay.get(`${hubIdArg}|${d}`) ?? 0),
    [plan.hubDayOwn, receivedByHubDay]
  );
  // Sortation ÷ own for the filtered hubs on a date — scales own package mix up to
  // total handled (own + received-in) for MHE/package checks.
  const sortRatioOf = useCallback(
    (d: string) => {
      let own = 0;
      let srt = 0;
      for (const h of hubSet) {
        own += plan.hubDayOwn.get(`${h}|${d}`) ?? 0;
        srt += sortDayOf(h, d);
      }
      return own > 0 ? srt / own : 1;
    },
    [hubSet, plan.hubDayOwn, sortDayOf]
  );

  // MHE over-capacity callouts (sortation basis): each type's per-day own volume
  // scaled to total handled (× sort ratio), vs its daily MHE cap.
  const mheBreaches = useMemo(() => {
    const out: Array<{ type: "cartons" | "polybags" | "irregular"; days: number; worstDate: string; worst: number; cap: number }> = [];
    (["cartons", "polybags", "irregular"] as const).forEach((k) => {
      const cap = mheCaps[k];
      if (cap == null || cap <= 0) return;
      let days = 0;
      let worst = 0;
      let worstDate = "";
      for (const d of allDates) {
        const v = (plan.dayPkg.get(d)?.[k] ?? 0) * sortRatioOf(d);
        if (v > cap) days += 1;
        if (v > worst) ((worst = v), (worstDate = d));
      }
      if (days > 0) out.push({ type: k, days, worstDate, worst: Math.round(worst), cap });
    });
    return out;
  }, [mheCaps, allDates, plan.dayPkg, sortRatioOf]);

  // ── Columns: weeks, each expandable to its 7 days ──
  const cols: Col[] = useMemo(() => {
    const out: Col[] = [];
    weeks4.forEach((w, wi) => {
      if (openWeeks.has(w.weekStart)) {
        w.dates.forEach((d, di) =>
          out.push({ key: d, label: fmtDow(d).split(" ")[0], dates: [d], weekStart: w.weekStart, isDay: true, first: di === 0 })
        );
      } else {
        out.push({ key: w.weekStart, label: `W${wi + 1}`, sub: fmtDate(w.weekEnd), dates: w.dates, weekStart: w.weekStart, isDay: false, first: false });
      }
    });
    return out;
  }, [weeks4, openWeeks]);
  const toggleWeek = (ws: string) =>
    setOpenWeeks((s) => {
      const n = new Set(s);
      n.has(ws) ? n.delete(ws) : n.add(ws);
      return n;
    });

  // Row sets
  const hubRows: Row[] = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => {
          const byDate = new Map(allDates.map((d) => [d, plan.hubDayOwn.get(`${h.id}|${d}`) ?? 0]));
          // Peak-day util is on SORTATION (own + received-in), the real handling load.
          let peak = 0;
          for (const d of allDates) peak = Math.max(peak, sortDayOf(h.id, d));
          const util = h.capacity && h.capacity > 0 ? Math.round((peak / h.capacity) * 100) : null;
          const tone = util == null ? "text-slate-400" : util > 100 ? "text-red-600 font-semibold" : util > 85 ? "text-amber-600" : "text-emerald-600";
          return { id: h.id, label: h.code || h.name, byDate, color: hubColor.get(h.id), cap: h.capacity ?? null, right: <span className={tone}>{util == null ? "—" : `${util}%`}</span> };
        })
        .filter((r) => [...r.byDate.values()].some((v) => v > 0))
        .sort((a, b) => sum(b.byDate) - sum(a.byDate)),
    [hubs, hubSet, allDates, plan.hubDayOwn, hubColor, sortDayOf]
  );

  const custRows: Row[] = useMemo(() => {
    const ranked = [...plan.custTotal.entries()].sort((a, b) => b[1] - a[1]).slice(0, topN);
    const primaryHub = (cid: string) => {
      let best = "";
      let bv = -1;
      for (const h of hubSet) {
        const v = plan.custHub.get(`${cid}|${h}`) ?? 0;
        if (v > bv) ((bv = v), (best = h));
      }
      return best;
    };
    return ranked.map(([id]) => ({
      id,
      label: plan.custName.get(id) ?? id,
      byDate: new Map(allDates.map((d) => [d, plan.custDate.get(`${id}|${d}`) ?? 0])),
      color: hubColor.get(primaryHub(id)),
    }));
  }, [plan.custTotal, plan.custName, plan.custDate, plan.custHub, allDates, topN, hubSet, hubColor]);

  const branchRows: Row[] = useMemo(() => {
    const arr = [...branchDate.entries()].map(([id, bd]) => ({ id, bd, total: sum(bd) }));
    arr.sort((a, b) => b.total - a.total);
    return arr.slice(0, topN).map(({ id, bd }) => {
      const b = branches.find((x) => x.id === id);
      return { id, label: b?.name ?? id, byDate: bd, color: b ? hubColor.get(b.hubId) : undefined };
    });
  }, [branchDate, branches, topN, hubColor]);

  const lhRows4: Row[] = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => ({ id: h.id, label: h.code || h.name, byDate: new Map(allDates.map((d) => [d, lhLoadsInOnDay(lhRows, new Set([h.id]), d)])), color: hubColor.get(h.id) }))
        .filter((r) => sum(r.byDate) > 0)
        .sort((a, b) => sum(b.byDate) - sum(a.byDate)),
    [hubs, hubSet, allDates, lhRows, hubColor]
  );

  // Package rows (per type) for the WoW package table.
  const pkgRows: Row[] = useMemo(
    () =>
      (["cartons", "polybags", "irregular"] as const).map((k) => ({
        id: k,
        label: k.charAt(0).toUpperCase() + k.slice(1),
        // Sortation basis: scale own per-type volume to total handled (own + received-in).
        byDate: new Map(allDates.map((d) => [d, Math.round((plan.dayPkg.get(d)?.[k] ?? 0) * sortRatioOf(d))])),
        color: TYPE_COLORS[k],
        cap: mheCaps[k],
      })),
    [allDates, plan.dayPkg, mheCaps, sortRatioOf]
  );

  // Volume availability (hourly) week 1.
  const availW1 = useMemo(() => {
    const byHour = new Array<number>(24).fill(0);
    const opts = { overrides: overrideMap, events, loadCapacity: capByKey };
    for (const h of hubSet) for (const d of weeks4[0].dates) {
      const a = hourlyAvailability(customers, lhRows, h, d, opts);
      for (let i = 0; i < 24; i++) byHour[i] += a.byHour[i].total;
    }
    return byHour.map((v) => Math.round(v));
  }, [hubSet, weeks4, customers, lhRows, overrideMap, events, capByKey]);
  const peakHour = availW1.indexOf(Math.max(...availW1));

  // Events in the window + reroute impact.
  const winEnd = weeks4[3].weekEnd;
  const windowEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          e.active !== false &&
          e.startDate <= winEnd &&
          e.endDate >= lockWeek &&
          (isAll || hubSet.has(e.hubId) || (e.redistributeHubs ?? []).some((r) => hubSet.has(r.hubId)))
      ),
    [events, winEnd, lockWeek, isAll, hubSet]
  );
  const reroute = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => {
          let inn = 0;
          let out = 0;
          for (const d of allDates) {
            inn += reroutedInToHub(customers, h.id, d, events);
            out += reroutedOutOfHub(customers, h.id, d, events);
          }
          return { h, inn: Math.round(inn), out: Math.round(out) };
        })
        .filter((x) => x.inn > 0 || x.out > 0),
    [hubs, hubSet, allDates, customers, events]
  );

  // Hubs whose peak day exceeds daily capacity (for the Attention panel).
  const overCapHubs = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id) && h.capacity && h.capacity > 0)
        .map((h) => {
          let peak = 0;
          for (const d of allDates) peak = Math.max(peak, sortDayOf(h.id, d)); // sortation = own + received-in
          return { h, util: Math.round((peak / (h.capacity as number)) * 100) };
        })
        .filter((x) => x.util > 100)
        .sort((a, b) => b.util - a.util),
    [hubs, hubSet, allDates, sortDayOf]
  );

  // Daily actuals (filtered) keyed by date and by hub|date — for the daily chart + accuracy.
  const actualByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of actuals) if (hubSet.has(a.hubId)) m.set(a.date, (m.get(a.date) ?? 0) + a.actualVolume);
    return m;
  }, [actuals, hubSet]);
  const actualByHubDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of actuals) if (hubSet.has(a.hubId)) m.set(`${a.hubId}|${a.date}`, (m.get(`${a.hubId}|${a.date}`) ?? 0) + a.actualVolume);
    return m;
  }, [actuals, hubSet]);

  // Daily forecast (28 days) vs the same-weekday actual from the last 4 completed
  // weeks (28 days). Both start on a Saturday so index i is the same weekday.
  const dailyCompare = useMemo(() => {
    const past0 = addDays(nowWeek, -28);
    return allDates.map((d, i) => {
      let f = 0;
      for (const h of hubSet) f += plan.hubDayOwn.get(`${h}|${d}`) ?? 0;
      const ad = addDays(past0, i);
      const a = actualByDate.has(ad) ? (actualByDate.get(ad) ?? 0) : null;
      const parts = fmtDow(d).split(" ");
      return { weekday: parts[0], date: parts[1] ?? d.slice(5), a: Math.round(f), b: a == null ? null : Math.round(a) };
    });
  }, [allDates, hubSet, plan.hubDayOwn, actualByDate, nowWeek]);

  // Forecast accuracy per hub over the last 4 completed weeks (MAPE → accuracy %).
  const hubAccuracy = useMemo(() => {
    const past = Array.from({ length: 28 }, (_, i) => addDays(nowWeek, -28 + i));
    return hubs
      .filter((h) => hubSet.has(h.id))
      .map((h) => {
        const rows = past
          .map((d) => ({ forecast: forecastHubDay(customers, h.id, d, overrideMap, events), actual: actualByHubDate.get(`${h.id}|${d}`) ?? 0 }))
          .filter((r) => r.actual > 0);
        return { h, mape: meanAbsPctError(rows), n: rows.length };
      })
      .filter((x) => x.n > 0)
      .sort((a, b) => (a.mape ?? 999) - (b.mape ?? 999));
  }, [hubs, hubSet, customers, overrideMap, events, actualByHubDate, nowWeek]);

  // Per-hub plan drift vs the locked week-1 snapshot (where it differs from live).
  const hubDriftW1 = useMemo(() => {
    if (!lock) return [] as Array<{ h: Hub; live: number; locked: number; pct: number | null }>;
    const locked = lockedHubWeekTotals(lockCells);
    return hubs
      .filter((h) => hubSet.has(h.id))
      .map((h) => {
        const live = weeks4[0].dates.reduce((s, d) => s + (plan.hubDayOwn.get(`${h.id}|${d}`) ?? 0), 0);
        const lk = locked.get(h.id) ?? 0;
        return { h, live: Math.round(live), locked: Math.round(lk), pct: lk > 0 ? ((live - lk) / lk) * 100 : null };
      })
      .filter((x) => x.locked > 0 || x.live > 0)
      .sort((a, b) => Math.abs(b.pct ?? 0) - Math.abs(a.pct ?? 0));
  }, [lock, lockCells, hubs, hubSet, weeks4, plan.hubDayOwn]);

  // Drift (week 1).
  const lockedW1 = useMemo(() => {
    if (!lock) return null;
    const per = lockedHubWeekTotals(lockCells);
    let v = 0;
    for (const h of hubSet) v += per.get(h) ?? 0;
    return v;
  }, [lock, lockCells, hubSet]);
  const liveW1 = plan.weekTot[0];
  const driftPct = lockedW1 && lockedW1 > 0 ? ((liveW1 - lockedW1) / lockedW1) * 100 : null;

  const lockedWeeks = useMemo(() => new Set(locks.map((l) => l.weekStart)), [locks]);
  const weekOptions = useMemo(() => {
    const base = startOfWeekSat(today());
    return Array.from({ length: WEEK_OPTIONS }, (_, i) => addDays(base, i * 7));
  }, []);
  const hubName = useCallback((id: string) => hubs.find((h) => h.id === id)?.code || hubs.find((h) => h.id === id)?.name || "?", [hubs]);

  async function doLock() {
    if (!accountId || !isAdmin) return;
    setBusy(true);
    setError(null);
    try {
      const cells = buildLockCells(customers, hubs, weeks4[0].dates, overrideMap, events);
      await createLock(accountId, startWeek, cells, liveFlows as unknown as LockFlow[], { label, note, lockedBy: session?.user?.id ?? null });
      setLabel("");
      setNote("");
      setLocks(await listActiveLocks(accountId));
      await refreshLock();
    } catch (e) {
      setError(getErrorMessage(e, "Failed to lock the week."));
    } finally {
      setBusy(false);
    }
  }
  async function doUnlock() {
    if (!accountId || !isAdmin) return;
    if (!window.confirm("Unlock week 1? The frozen plan is kept for history but the week is no longer locked.")) return;
    setBusy(true);
    setError(null);
    try {
      await unlockWeek(accountId, startWeek);
      setLocks(await listActiveLocks(accountId));
      await refreshLock();
    } catch (e) {
      setError(getErrorMessage(e, "Failed to unlock the week."));
    } finally {
      setBusy(false);
    }
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }
  if (loading) return <Spinner />;

  const hubLabel = isAll ? "All hubs · network" : hubs.find((h) => h.id === hubId)?.name ?? "—";
  const peakWeekIdx = plan.weekTot.indexOf(Math.max(...plan.weekTot));
  const peakDay = allDates.reduce(
    (best, d) => {
      let v = 0;
      for (const h of hubSet) v += plan.hubDayOwn.get(`${h}|${d}`) ?? 0;
      return v > best.value ? { date: d, value: v } : best;
    },
    { date: "", value: 0 }
  );
  const filteredHubCols = hubs.filter((h) => hubSet.has(h.id)).map((h) => ({ id: h.id, label: h.code || h.name }));
  const allHubCols = hubs.map((h) => ({ id: h.id, label: h.code || h.name }));
  const dowCols = weeks4[0].dates.map((d, i) => ({ id: String(i), label: fmtDow(d).split(" ")[0] }));

  // Forecast-by-week chart: 3 trailing ACTUAL weeks + 4 forecast weeks, with a
  // dashed trend line and per-bar WoW % deltas.
  const haveActuals = actualWeeks.some((w) => w.total > 0);
  const byWeekSeq = [
    ...(haveActuals ? actualWeeks.map((w, i) => ({ value: Math.round(w.total), date: w.weekEnd, label: `${3 - i}w ago`, color: "#cbd5e1", wi: -1 })) : []),
    ...weeks4.map((w, i) => ({ value: Math.round(plan.weekTot[i]), date: w.weekEnd, label: `W${i + 1}`, color: WEEK_COLORS[i], wi: i })),
  ];
  const byWeekBars = byWeekSeq.map((s, i) => {
    const prev = i > 0 ? byWeekSeq[i - 1].value : null;
    const d = prev && prev > 0 ? ((s.value - prev) / prev) * 100 : null;
    return {
      label: s.label,
      value: s.value,
      color: s.color,
      sub: fmtDate(s.date),
      delta: d == null ? undefined : `${d > 0 ? "+" : ""}${d.toFixed(0)}%`,
      deltaTone: (d == null ? "flat" : d > 0.5 ? "up" : d < -0.5 ? "down" : "flat") as "up" | "down" | "flat",
    };
  });
  const w1ChartIdx = haveActuals ? 3 : 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Forecast Planner · week-over-week"
        subtitle="The same plan as Forecast Lock, broken out per week (W1→W4) with day drill-down and an events/reroute watch."
      >
        <span className="text-xs text-slate-400">{hubLabel}</span>
      </PageHeader>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-56">
            <Field label="Hub">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                <option value={ALL}>All hubs (network)</option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-60">
            <Field label="Start week (week 1 = lock target)">
              <Select value={startWeek} onChange={(e) => setStartWeek(e.target.value)}>
                {weekOptions.map((w) => (
                  <option key={w} value={w}>
                    {fmtDate(w)} – {fmtDate(addDays(w, 6))}
                    {lockedWeeks.has(w) ? "  🔒" : ""}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-32">
            <Field label="Top rows">
              <Select value={String(topN)} onChange={(e) => setTopN(Number(e.target.value))}>
                <option value="5">Top 5</option>
                <option value="8">Top 8</option>
                <option value="12">Top 12</option>
                <option value="999">All</option>
              </Select>
            </Field>
            <label className="flex cursor-pointer items-center gap-2 pb-2 text-xs text-slate-600" title="Land H2H/Direct inbound on ship date + transit days instead of same-day">
              <input type="checkbox" checked={transitAware} onChange={(e) => setTransitAware(e.target.checked)} />
              Transit-aware arrivals
            </label>
          </div>
          {openWeeks.size > 0 && (
            <Button variant="ghost" onClick={() => setOpenWeeks(new Set())}>Collapse all days</Button>
          )}
          <p className="text-xs text-slate-400">Click a week header (▸) to expand it to days.{transitAware ? " Branch arrivals are transit-aware (ship + transit days)." : ""}</p>
        </div>
      </Card>

      {/* Lock bar */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          {lock ? (
            <>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">🔒 Week 1 locked</span>
                <p className="mt-1 text-xs text-slate-500">
                  {lock.label ? <b className="text-slate-700">{lock.label} · </b> : null}
                  locked {fmtDate(lock.lockedAt.slice(0, 10))} · {fmtDate(lockWeek)}–{fmtDate(weeks4[0].weekEnd)}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="ghost" onClick={doUnlock} disabled={!isAdmin || busy}>Unlock</Button>
                <Button onClick={doLock} disabled={!isAdmin || busy}>{busy ? "Working…" : "Re-lock (refresh snapshot)"}</Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-44">
                <Field label="Label (optional)">
                  <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Wk26 plan" />
                </Field>
              </div>
              <div className="w-72">
                <Field label="Note (optional)">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this plan is committed…" />
                </Field>
              </div>
              <Button onClick={doLock} disabled={!isAdmin || busy}>{busy ? "Locking…" : "🔒 Lock week 1"}</Button>
            </>
          )}
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
          ⓘ <b>Changing a locked plan:</b> adjust inputs (customers, calibration, events, overrides), watch the
          <b> drift</b> grow, then <b>Re-lock</b> to commit (the old snapshot is kept as history). <b>Unlock</b> abandons it.
          {!isAdmin && <span className="text-amber-600"> View-only — only admins can lock/unlock.</span>}
        </p>
      </Card>

      {/* Per-week KPI comparison */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {weeks4.map((w, i) => {
          const v = plan.weekTot[i];
          const prev = i > 0 ? plan.weekTot[i - 1] : null;
          const d = prev && prev > 0 ? ((v - prev) / prev) * 100 : null;
          return (
            <Kpi
              key={w.weekStart}
              label={`Week ${i + 1}${i === 0 ? " 🔒" : ""}`}
              value={fmtN(v)}
              sub={`${fmtDate(w.weekEnd)}${d == null ? "" : ` · ${d > 0 ? "+" : ""}${d.toFixed(1)}% WoW`}`}
              tone={d == null ? undefined : Math.abs(d) > 15 ? "bad" : Math.abs(d) > 7 ? "warn" : "good"}
            />
          );
        })}
        <Kpi
          label="Drift vs locked (W1)"
          value={driftPct == null ? "—" : `${driftPct > 0 ? "+" : ""}${driftPct.toFixed(1)}%`}
          sub={lock ? "live − locked" : "week 1 not locked"}
          tone={driftPct == null ? undefined : Math.abs(driftPct) > 10 ? "bad" : Math.abs(driftPct) > 3 ? "warn" : "good"}
        />
      </div>

      {/* Attention — what needs a decision, at a glance */}
      <Card className={overCapHubs.length || mheBreaches.length || windowEvents.length ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/40"}>
        <div className="mb-1.5 text-sm font-medium text-slate-700">Needs attention · {fmtDate(weeks4[0].weekStart)}–{fmtDate(weeks4[3].weekEnd)}</div>
        {overCapHubs.length === 0 && mheBreaches.length === 0 && windowEvents.length === 0 ? (
          <p className="text-sm text-emerald-700">✓ All clear — no hubs over capacity, no MHE breaches, and no events or reroutes in this window.</p>
        ) : (
          <ul className="space-y-1 text-sm text-slate-700">
            {overCapHubs.length > 0 && (
              <li>
                🔴 <b>{overCapHubs.length} hub{overCapHubs.length > 1 ? "s" : ""} over capacity:</b>{" "}
                {overCapHubs.slice(0, 6).map((x) => `${x.h.code || x.h.name} ${x.util}%`).join(", ")}
                {overCapHubs.length > 6 ? "…" : ""}
              </li>
            )}
            {mheBreaches.length > 0 && (
              <li>
                🟠 <b>MHE over capacity:</b>{" "}
                {mheBreaches.map((b) => `${b.type.charAt(0).toUpperCase() + b.type.slice(1)} (${b.days}d, worst ${fmtDate(b.worstDate)})`).join(", ")}
              </li>
            )}
            {windowEvents.length > 0 && (
              <li>
                🟣 <b>{windowEvents.length} event{windowEvents.length > 1 ? "s" : ""} / reroute{windowEvents.length > 1 ? "s" : ""}</b> active —{" "}
                {windowEvents.slice(0, 4).map((e) => `${hubName(e.hubId)} ${EVENT_LABELS[e.type] ?? e.type}`).join(", ")}
                {windowEvents.length > 4 ? "…" : ""}
              </li>
            )}
            <li className="text-slate-500">
              📈 Peak week <b>W{peakWeekIdx + 1}</b> ({fmtN(plan.weekTot[peakWeekIdx])}); peak day <b>{peakDay.date ? fmtDate(peakDay.date) : "—"}</b> ({fmtN(peakDay.value)}).
            </li>
          </ul>
        )}
      </Card>

      {/* Daily: 28-day forecast vs same-weekday actuals (prior 4 weeks) */}
      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Daily forecast vs recent actuals · 28 days</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-3 rounded-sm bg-teal-700" /> forecast (next 28d)</span>
            <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 border-t border-dashed border-slate-500" /> actual (same weekday, last 4 wks)</span>
          </div>
        </div>
        <p className="mb-2 text-[11px] text-slate-400">All 28 days (Sat→Fri). Bars = forecast; dashed line = the actual from the same weekday in the trailing 4 completed weeks. {hubLabel}.</p>
        <DailyBarsLine points={dailyCompare} />
      </Card>

      {/* Forecast volume by week — with WoW deltas + last-3-weeks actuals trend line */}
      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Forecast volume by week</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-slate-300" /> last 3 wks actual</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-teal-700" /> forecast</span>
            <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 bg-slate-500" /> trend</span>
          </div>
        </div>
        <p className="mb-2 text-[11px] text-slate-400">
          Each week separately with week-over-week %{haveActuals ? " — and the trailing 3 weeks of actuals for context" : ""}. Week 1 = lock target (highlighted). {hubLabel}.
        </p>
        <BarsChart data={byWeekBars} highlightIndex={w1ChartIdx} showLine lineLabel="dashed = WoW trend" />
      </Card>

      {/* Plan drift vs locked (week 1) */}
      {lock && hubDriftW1.length > 0 && (
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">Plan drift vs locked · week 1</div>
            <p className="mt-0.5 text-[11px] text-slate-400">Per-hub live forecast now vs the committed week-1 snapshot — where the plan has moved since you locked it.</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Hub</th>
                <th className="px-3 py-2 text-right font-medium">Locked</th>
                <th className="px-3 py-2 text-right font-medium">Live now</th>
                <th className="px-3 py-2 text-right font-medium">Δ</th>
                <th className="px-5 py-2 text-right font-medium">Δ%</th>
              </tr>
            </thead>
            <tbody>
              {hubDriftW1.map((r) => {
                const delta = r.live - r.locked;
                return (
                  <tr key={r.h.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-1.5 font-medium text-slate-800">{r.h.code || r.h.name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtN(r.locked)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtN(r.live)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-slate-400"}`}>{delta > 0 ? "+" : ""}{fmtN(delta)}</td>
                    <td className={`px-5 py-1.5 text-right tabular-nums font-medium ${r.pct == null ? "text-slate-400" : Math.abs(r.pct) > 10 ? "text-red-600" : Math.abs(r.pct) > 3 ? "text-amber-600" : "text-slate-500"}`}>
                      {r.pct == null ? "—" : `${r.pct > 0 ? "+" : ""}${r.pct.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* WoW tables */}
      <WowTable title="Volume by hub · week-over-week" hint="Each week separate; click ▸ to drill to days. Cells turn red on days a hub exceeds its daily capacity. Right column = peak-day util." firstHeader="Hub" rightHeader="Peak util" cols={cols} rows={hubRows} allDates={allDates} weeks4={weeks4} lockWeek={lockWeek} onToggle={toggleWeek} />

      {/* Forecast accuracy per hub (last 4 weeks) */}
      {hubAccuracy.length > 0 && (
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">Forecast accuracy by hub · last 4 weeks</div>
            <p className="mt-0.5 text-[11px] text-slate-400">How close recent forecasts were to actuals (100% − MAPE). Higher = more trustworthy numbers for that hub.</p>
          </div>
          <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {hubAccuracy.map(({ h, mape }) => {
              const acc = mape == null ? null : Math.max(0, 100 - mape);
              const tone = acc == null ? "text-slate-400" : acc >= 90 ? "text-emerald-600" : acc >= 80 ? "text-amber-600" : "text-red-600";
              return (
                <div key={h.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                  <span className="text-sm font-medium text-slate-700">{h.code || h.name}</span>
                  <span className={`text-sm font-semibold tabular-nums ${tone}`}>{acc == null ? "—" : `${acc.toFixed(0)}%`}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      <WowTable title="Top customers · week-over-week" hint="Largest shippers, split per week." firstHeader="Customer" cols={cols} rows={custRows} allDates={allDates} weeks4={weeks4} lockWeek={lockWeek} onToggle={toggleWeek} />

      {/* Customer forecast by hub matrix (4-week totals) */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Customer forecast by hub · 4 weeks</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Top customers × hub (only columns with volume). Darker = more.</p>
        </div>
        <HeatMatrix
          rowHeader="Customer"
          rows={custRows.map((r) => ({ id: r.id, name: r.label }))}
          cols={filteredHubCols}
          value={(cid, hid) => plan.custHub.get(`${cid}|${hid}`) ?? 0}
        />
      </Card>

      <WowTable title="Top branches · week-over-week" hint="Network-flow volume delivered to each branch, per week." firstHeader="Branch" cols={cols} rows={branchRows} allDates={allDates} weeks4={weeks4} lockWeek={lockWeek} onToggle={toggleWeek} />

      {/* Branch forecast by origin hub matrix (4-week totals) */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Branch forecast by origin hub · 4 weeks</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Which hub feeds each branch (Local + H2H). Darker = more; the bold cell is each branch's biggest source.</p>
        </div>
        <HeatMatrix
          rowHeader="Branch"
          rows={branchRows.map((r) => ({ id: r.id, name: r.label }))}
          cols={allHubCols}
          value={(bid, hid) => Math.round((hubOwn4.get(hid) ?? 0) * shareOf(shares, hid, bid))}
        />
      </Card>

      {/* Package type by week — with MHE over-capacity highlighting */}
      {mheBreaches.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          ⚠ <b>MHE over capacity:</b>{" "}
          {mheBreaches
            .map((b) => `${b.type.charAt(0).toUpperCase() + b.type.slice(1)} exceeds its ${fmtN(b.cap)}/day cap on ${b.days} day(s) — worst ${fmtDate(b.worstDate)} (${fmtN(b.worst)})`)
            .join("; ")}
          .
        </div>
      )}
      <WowTable
        title="Package type · week-over-week"
        hint="Volume by handling type, split per week. Cells turn red on days that exceed the hub MHE daily capacity for that type."
        firstHeader="Type"
        cols={cols}
        rows={pkgRows}
        allDates={allDates}
        weeks4={weeks4}
        lockWeek={lockWeek}
        onToggle={toggleWeek}
      />

      {/* Package type by hub matrix (4-week totals) */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Package type by hub · 4 weeks</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Which hub handles most of each handling type. Darker = more; the bold cell is each type's biggest hub.</p>
        </div>
        <HeatMatrix
          rowHeader="Package type"
          rows={[
            { id: "cartons", name: "Cartons" },
            { id: "polybags", name: "Polybags" },
            { id: "irregular", name: "Irregular" },
          ]}
          cols={filteredHubCols}
          value={(tid, hid) => {
            const sp = plan.hubPkg.get(hid);
            return sp ? Math.round(sp[tid as "cartons" | "polybags" | "irregular"]) : 0;
          }}
        />
      </Card>

      <WowTable title="LH loads inbound by hub · week-over-week" hint="Scheduled linehaul loads arriving each week." firstHeader="Hub" cols={cols} rows={lhRows4} allDates={allDates} weeks4={weeks4} lockWeek={lockWeek} onToggle={toggleWeek} integer />

      {/* LH loads by weekday matrix (per-week arrival pattern) */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">LH loads by weekday · per week</div>
          <p className="mt-0.5 text-[11px] text-slate-400">When inbound linehaul lands each week, by hub. Darker = more; the bold cell is each hub's busiest day — plan inbound staffing around it.</p>
        </div>
        <HeatMatrix
          rowHeader="Hub"
          rows={lhRows4.map((r) => ({ id: r.id, name: r.label }))}
          cols={dowCols}
          value={(hid, colId) => lhLoadsInOnDay(lhRows, new Set([hid]), weeks4[0].dates[Number(colId)])}
          integer
        />
      </Card>

      {/* Events & reroutes */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Events &amp; reroutes in this window</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Every hub event overlapping {fmtDate(lockWeek)}–{fmtDate(winEnd)} that affects the selected scope — so nothing is missed.</p>
        </div>
        {windowEvents.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">No hub events or reroutes scheduled in this window.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Hub</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Dates</th>
                <th className="px-3 py-2 text-right font-medium">Capacity</th>
                <th className="px-3 py-2 font-medium">Scope</th>
                <th className="px-5 py-2 font-medium">Redistributes to</th>
              </tr>
            </thead>
            <tbody>
              {windowEvents.map((e) => {
                const retained = e.capacityPct == null ? (e.type === "closure" ? 0 : 100) : e.capacityPct;
                const scope = e.mode === "by_customer" ? `${(e.customerOverrides ?? []).length} customer(s)` : "Whole hub";
                const redis = e.redistributeVolume && e.redistributeHubs?.length ? e.redistributeHubs.map((r) => `${hubName(r.hubId)} ${r.pct}%`).join(" · ") : "—";
                return (
                  <tr key={e.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2 font-medium text-slate-800">{hubName(e.hubId)}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${e.type === "closure" ? "bg-red-50 text-red-700" : e.type === "reroute" ? "bg-violet-50 text-violet-700" : e.type === "expansion" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {EVENT_LABELS[e.type] ?? e.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{fmtDate(e.startDate)} – {fmtDate(e.endDate)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${retained < 100 ? "text-red-600" : "text-slate-600"}`}>{retained}%</td>
                    <td className="px-3 py-2 text-slate-600">{scope}</td>
                    <td className="px-5 py-2 text-slate-600">{redis}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {reroute.length > 0 && (
          <div className="border-t border-slate-200 px-5 py-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Reroute volume impact · 4 weeks</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {reroute.map((r) => (
                <div key={r.h.id} className="rounded-lg border border-slate-200 px-3 py-2 text-xs">
                  <div className="font-medium text-slate-700">{r.h.code || r.h.name}</div>
                  <div className="mt-1 flex gap-4">
                    <span className="text-emerald-600">+{fmtN(r.inn)} in</span>
                    <span className="text-red-600">−{fmtN(r.out)} out</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Volume availability */}
      <Card>
        <div className="mb-1 text-sm font-medium text-slate-700">Volume availability · week 1 hourly profile</div>
        <p className="mb-2 text-[11px] text-slate-400">When volume lands and is available to sort across the day, {hubLabel}. Peak hour: <b className="text-slate-600">{String(peakHour).padStart(2, "0")}:00</b>.</p>
        <HourBars byHour={availW1} />
      </Card>

      {lock?.note && (
        <Card>
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Lock note</p>
          <p className="mt-1 text-sm text-slate-700">{lock.note}</p>
        </Card>
      )}
    </div>
  );
}

function sum(m: Map<string, number>): number {
  let s = 0;
  for (const v of m.values()) s += v;
  return s;
}

function HeatMatrix({
  rowHeader,
  rows,
  cols,
  value,
  integer,
}: {
  rowHeader: string;
  rows: Array<{ id: string; name: string }>;
  cols: Array<{ id: string; label: string }>;
  value: (rowId: string, colId: string) => number;
  integer?: boolean;
}) {
  const useCols = cols.filter((c) => rows.some((r) => value(r.id, c.id) > 0));
  const max = Math.max(1, ...rows.flatMap((r) => useCols.map((c) => value(r.id, c.id))));
  const fmtV = (n: number) => (integer ? String(Math.round(n)) : fmtN(n));
  if (useCols.length === 0) return <p className="px-5 py-8 text-center text-sm text-slate-400">No volume.</p>;
  return (
    <div className="max-h-[50vh] overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
            <th className="sticky left-0 top-0 z-20 bg-white px-4 py-2 text-left font-medium">{rowHeader}</th>
            {useCols.map((c) => (
              <th key={c.id} className="px-3 py-2 text-right font-medium">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const best = useCols.reduce((b, c) => (value(r.id, c.id) > value(r.id, b.id) ? c : b), useCols[0]);
            return (
              <tr key={r.id} className="border-b border-slate-100 last:border-0">
                <td className="sticky left-0 z-10 bg-white px-4 py-1.5 font-medium text-slate-700">{r.name}</td>
                {useCols.map((c) => {
                  const v = value(r.id, c.id);
                  const alpha = v > 0 ? 0.08 + 0.5 * (v / max) : 0;
                  const isBest = c.id === best.id && v > 0;
                  return (
                    <td
                      key={c.id}
                      className={`px-3 py-1.5 text-right tabular-nums ${isBest ? "font-semibold text-slate-900" : "text-slate-700"}`}
                      style={v > 0 ? { backgroundColor: `rgba(15,118,110,${alpha})` } : undefined}
                    >
                      {v > 0 ? fmtV(v) : "·"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "good" | "warn" | "bad" }) {
  const c = tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "text-slate-900";
  const dot = tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : tone === "good" ? "bg-emerald-500" : "";
  return (
    <Card>
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {dot && <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${c}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function WowTable({
  title,
  hint,
  firstHeader,
  rightHeader,
  cols,
  rows,
  allDates,
  weeks4,
  lockWeek,
  onToggle,
  integer,
  showWoW = true,
}: {
  title: string;
  hint?: string;
  firstHeader: string;
  rightHeader?: string;
  cols: Col[];
  rows: Row[];
  allDates: string[];
  weeks4: Array<{ weekStart: string; weekEnd: string; dates: string[] }>;
  lockWeek: string;
  onToggle: (weekStart: string) => void;
  integer?: boolean;
  showWoW?: boolean;
}) {
  const cell = (byDate: Map<string, number>, dates: string[]) => dates.reduce((s, d) => s + (byDate.get(d) ?? 0), 0);
  const fmtV = (n: number) => (integer ? String(Math.round(n)) : fmtN(n));
  const weekIdx = new Map(weeks4.map((w, i) => [w.weekStart, i]));
  const dayOver = (r: Row, d: string) => r.cap != null && r.cap > 0 && (r.byDate.get(d) ?? 0) > r.cap;
  const anyCap = rows.some((r) => r.cap != null);
  return (
    <Card className="p-0">
      <div className="border-b border-slate-200 px-5 py-2.5">
        <div className="text-sm font-medium text-slate-700">{title}</div>
        {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-400">No volume.</p>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                <th className="sticky left-0 top-0 z-20 bg-white px-4 py-2 text-left font-medium">{firstHeader}</th>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={c.isDay ? (c.first ? () => onToggle(c.weekStart) : undefined) : () => onToggle(c.weekStart)}
                    className={`px-2 py-2 text-right font-medium tabular-nums ${!c.isDay || c.first ? "cursor-pointer hover:text-brand-700" : ""} ${c.weekStart === lockWeek ? "bg-teal-50/60" : ""} ${c.isDay && c.first ? "border-l border-slate-200" : ""}`}
                    title={c.isDay ? "Click to collapse this week" : "Click to expand this week to days"}
                  >
                    {c.isDay ? (c.first ? "▾ " : "") : "▸ "}
                    {c.label}
                    {c.sub && <span className="ml-1 font-normal text-slate-400">{c.sub}</span>}
                  </th>
                ))}
                <th className="px-4 py-2 text-right font-medium">4-wk</th>
                {rightHeader && <th className="px-4 py-2 text-right font-medium">{rightHeader}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const wt = weeks4.map((w) => cell(r.byDate, w.dates));
                return (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0">
                    <td className="sticky left-0 z-10 bg-white px-4 py-1.5 font-medium text-slate-700">
                      {r.label}
                      {r.cap != null && <span className="ml-1 text-[10px] font-normal text-slate-400">cap {fmtN(r.cap)}/d</span>}
                    </td>
                    {cols.map((c) => {
                      const val = cell(r.byDate, c.dates);
                      const overDays = c.isDay ? (dayOver(r, c.key) ? [c.key] : []) : c.dates.filter((d) => dayOver(r, d));
                      const over = overDays.length > 0;
                      const wi = weekIdx.get(c.weekStart) ?? 0;
                      const prev = !c.isDay && wi > 0 ? wt[wi - 1] : null;
                      const wow = showWoW && prev != null && prev > 0 ? ((val - prev) / prev) * 100 : null;
                      return (
                        <td
                          key={c.key}
                          title={over ? `Over capacity: ${overDays.map(fmtDate).join(", ")}` : undefined}
                          className={`px-2 py-1.5 text-right tabular-nums ${over ? "bg-red-100 font-semibold text-red-700" : c.weekStart === lockWeek ? "bg-teal-50/40 text-slate-700" : "text-slate-700"}`}
                        >
                          <div>{over && !c.isDay ? "⚠ " : ""}{fmtV(val)}</div>
                          {wow != null && (
                            <div className={`text-[9px] font-medium ${wow > 0.5 ? "text-emerald-600" : wow < -0.5 ? "text-red-500" : "text-slate-400"}`}>
                              {wow > 0 ? "+" : ""}{wow.toFixed(0)}%
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-1.5 text-right align-top tabular-nums font-semibold text-slate-800">{fmtV(cell(r.byDate, allDates))}</td>
                    {rightHeader && <td className="px-4 py-1.5 text-right align-top tabular-nums">{r.right ?? "—"}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {anyCap && (
        <p className="border-t border-slate-100 px-5 py-1.5 text-[10px] text-slate-400">
          <span className="mr-1 inline-block h-2 w-3 rounded-sm bg-red-100 align-middle" /> red = a day exceeds capacity (week cells flag ⚠ when any day inside is over).
        </p>
      )}
    </Card>
  );
}
